import { customElement, state } from 'lit/decorators.js';
import { BaseComponent } from '../../base-component/base-component';
import { css, html, TemplateResult } from 'lit';
import { WishList } from '../interfaces/wish-list-items.interface';
import store, { waitForUser } from '../../../store/store';
import { selectUser } from '../../user-profile/user-profile.selectors';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { StoreState } from '../../../store/store.interface';
import { fetchWishListByWishListId } from '../thunks/wish-list-fetch-wish-list-by-id.thunks';
import {
  _renderLoadingTemplate,
  getSharedWishListUrl,
  getWishlistShareLinkOffCanvasTitle,
  isEditWishListSuccess,
  isWishListFailure,
  isWishListLoadingOrIdle,
  isWishListSuccess,
  redirectToMyLists,
  renderNoWishListItemsTemplate,
} from '../utils/wish-list.util';
import { getWishListData } from '../store/wish-list.selector';
import { loadCustomer } from '../../customer/customer';
import { connect } from 'pwa-helpers';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import { checkForRedirectToLogin } from '../../../common/utils/login-utils';
import { isAemPage } from '../../../common/utils/aem-utils';
import {
  openDeleteWishListOffCanvas,
  openShareWishListOffCanvas,
  openUpdateWishListOffCanvas,
} from '../utils/wish-list-offcanvas.util';
import { renderErrorToast } from '../utils/wish-list-toast.util';
import '../../toast/toast';
import '../../product-card/variations/k12-product-list.display';
import '../../product-card/product-card-display/product-card-image.display';
import '../components/wish-list-item-card-display';
import '../components/wish-list-item-header';
import './wish-list-update-off-canvas';
import { getShareLinkOffCanvasContent } from '../../off-canvas/common-off-canvases/utils/common-off-canvas.util';

@customElement('mhecomm-wish-list-items')
export class WishListItems extends connect(store)(BaseComponent) {
  static styles = css`
    .custom-input {
      height: 24px;
      padding: 0.5px;
      font-size: 14px;
      margin-right: 4px;
    }

    .input-group.custom-input {
      width: 60px;
    }

    .custom-button {
      height: 30px;
      width: 150px;
    }

    .btn.custom-button {
      padding: 0 !important;
    }
  `;

  @state()
  wishList: WishList | undefined;

  @state()
  private user: UserProfile | undefined;

  @state()
  wishListLoadingOrIdle = false;

  @state()
  wishListSuccess = false;

  @state()
  wishListFetchCallFailed = false;

  private wishListBackButtonId = 'mhecom-back-to-my-lists';
  private wishListBackButtonLabel = 'Back to My Lists';

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  async connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    store.dispatch(loadCustomer());
    this.addEventListener('click', this.onWishlistOptionTrigger);
    waitForUser().then(() => {
      this.getWishListItems();
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onWishlistOptionTrigger);
  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);

    const wishListId = this.getWishListIdFromUrlParam();

    if (wishListId) {
      await store.dispatch(fetchWishListByWishListId(Number(wishListId))).then(() => {
        const wishList: WishList | undefined = getWishListData(store.getState() as StoreState);
        wishList && this.wishListAnalytics.wishListItems(this, wishList, this.user!);
      });
    }
  }

  async stateChanged(state: StoreState) {
    this.verifyUserIsLoggedIn(state);
    this.wishListLoadingOrIdle = isWishListLoadingOrIdle() && !this.wishList;
    this.wishListSuccess = isWishListSuccess() && !isEditWishListSuccess();
    this.wishListFetchCallFailed = isWishListFailure();

    if (this.wishListSuccess) {
      this.wishList = getWishListData(state);
    }
  }

  verifyUserIsLoggedIn(state: StoreState): void {
    this.user = selectUser(state);
    checkForRedirectToLogin(state.userProfileState.userProfileResponse);
  }

  private getWishListIdFromUrlParam() {
    return new URL(location.href).searchParams.get('wishListId');
  }

  getWishListItems() {
    const wishListId = this.getWishListIdFromUrlParam();
    if (wishListId) {
      store.dispatch(fetchWishListByWishListId(Number(wishListId)));
    } else {
      if (!isAemPage()) {
        redirectToMyLists();
      }
    }
  }

  renderComp() {
    return this.wishListFetchCallFailed
      ? renderErrorToast({
        error: ErrorToastWishList.FETCHING_WISH_LIST_ITEMS,
        displayLoadingTemplate: true,
        autoHide: false,
        hideClose: true,
      })
      : html`
        <div class="container-fluid">
          <button
            aria-label="${this.wishListBackButtonLabel}"
            id="${this.wishListBackButtonId}"
            class="btn btn-link ps-0 mb-2"
            data-automation-id="wish-list-back-button"
            @click="${this.onClickBackToMyLists}"
          >
            ${this.uxtIconReady ? html` <uxt-icon icon="chevron-left" class="pe-2"></uxt-icon>` : ''}
            ${this.wishListBackButtonLabel}
          </button>
          <mhecomm-wish-list-item-header .wishList=${this.wishList}></mhecomm-wish-list-item-header>
          ${this.wishList ? this.renderItems() : _renderLoadingTemplate()}
          ${this.renderWishListOffCanvas()}
        </div>
      `;
  }

  /**
   * Initialises the off-canvas for wish list actions.
   */
  renderWishListOffCanvas(): TemplateResult {
    return html` <div>
      <mhecomm-wish-list-delete-off-canvas
        wishListId="${this.wishList?.wishlist_id!}"
        wishListName="${this.wishList?.name!}"
        ?isWishListItemPage=${true}
      ></mhecomm-wish-list-delete-off-canvas>
      <mhecomm-wish-list-update-off-canvas
        wishListId=${this.wishList?.wishlist_id!}
        wishListName=${this.wishList?.name!}
        ?isWishListItemPage=${true}
      ></mhecomm-wish-list-update-off-canvas>
      ${getShareLinkOffCanvasContent(
        getWishlistShareLinkOffCanvasTitle(this.wishList?.name ?? ''),
        getSharedWishListUrl(this.wishList?.sharing_code ?? ''),
        false,
        false,
        () => this.wishListAnalytics.shareWishList(
          this, 
          this.wishList?.wishlist_id!, 
          this.wishList?.name!, 
          getSharedWishListUrl(this.wishList?.sharing_code ?? ''), 
          this.user!
        )!
      )}
    </div>`;
  }

  renderItems() {
    if (this.wishList?.wishlist_items && this.wishList.wishlist_items.length > 0) {
      return this.renderContent();
    } else {
      return this._renderNoContentTemplate();
    }
  }

  _renderNoContentTemplate() {
    return html`
      <div class="container-fluid">
        ${renderNoWishListItemsTemplate({
          title: 'Your list is empty!',
          message: 'Start adding items to revisit them and share with others.',
        })}
      </div>
    `;
  }

  renderContent() {
    return html`
      <mhecomm-paginated-wish-list-items .wishList="${this.wishList!}"></mhecomm-paginated-wish-list-items>
    `;
  }

  /**
   * Handles the click event to navigate back to the wish list page.
   */
  private onClickBackToMyLists(): void {
    this.wishListAnalytics.backToMyLists(this, this.wishListBackButtonId, this.wishListBackButtonLabel, this.user!);
    redirectToMyLists();
  }

  /**
   * Handles the wish list option selected by the user.
   *
   * @param event
   */
  private onWishlistOptionTrigger(event: CustomEventInit): void {
    const eventName = event?.detail?.eventName;
    if (eventName !== 'wish-list-option-selection') {
      return;
    }

    const wishList = event?.detail?.wishList;
    if (!wishList) {
      console.error('Wish list not found, the action could not be performed.');
    }

    this.wishList = wishList;

    switch (event?.detail?.option) {
      case WishListDropdownConstants.DELETE:
        openDeleteWishListOffCanvas(this.wishList?.wishlist_id!);
        break;
      case WishListDropdownConstants.EDIT_NAME:
        openUpdateWishListOffCanvas(this.wishList!);
        break;
      case WishListDropdownConstants.SHARE:
        openShareWishListOffCanvas(this.wishList!.wishlist_id);
        break;
      default:
        console.error('Wish list action not supported.');
        break;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-items': WishListItems;
  }
}